// ══════════════════════════════════════════════════════════════════
//  ED CRM - Configuração Central
//  Versão: 1.0 · Abril 2026
// ══════════════════════════════════════════════════════════════════

// ───────────────────────────────────────────────────────────────────
//  SUPABASE CONFIGURAÇÃO
// ───────────────────────────────────────────────────────────────────
export interface SupabaseConfig {
  url: string;
  anonKey: string;
  serviceRoleKey: string;
}

export const supabaseConfig: SupabaseConfig = {
  url: process.env.REACT_APP_SUPABASE_URL || localStorage.getItem('ed_supa_url') || '',
  anonKey: process.env.REACT_APP_SUPABASE_ANON_KEY || localStorage.getItem('ed_supa_key') || '',
  // Aviso: expor o service_role no bundle do browser permite que qualquer
  // pessoa que abra a aba de DevTools obtenha acesso administrativo total
  // ao Supabase. Em producao, mova auth.admin.createUser para uma Edge Function.
  serviceRoleKey:
    process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY ||
    process.env.SUPABASE_SERVICE_ROLE_KEY ||
    '',
};

// ───────────────────────────────────────────────────────────────────
//  EDGE FUNCTIONS
// ───────────────────────────────────────────────────────────────────
export const edgeFunctions = {
  ingestLead: 'ingest-lead',
  syncLead: 'sync-lead',
  webhookHandler: 'webhook-handler',
  adminInfraMetrics: 'admin-infra-metrics',
  partnerCrmApi: 'partner-crm-api',
  // Porta de saída dos eventos comerciais (webhook por workspace).
  workspaceWebhook: 'workspace-webhook',
} as const;

// ───────────────────────────────────────────────────────────────────
//  WEBHOOKS N8N
// ───────────────────────────────────────────────────────────────────
export const webhooks = {
  summary: process.env.REACT_APP_N8N_WEBHOOK_SUMMARY || localStorage.getItem('ed_n8n_summary') || '',
  vono: process.env.REACT_APP_N8N_WEBHOOK_VONO || localStorage.getItem('ed_n8n_vono') || '',
  call: process.env.REACT_APP_N8N_WEBHOOK_CALL || localStorage.getItem('ed_n8n_call') || '',
};

// ───────────────────────────────────────────────────────────────────
//  INTEGRAÇÃO IA
// ───────────────────────────────────────────────────────────────────
export const aiConfig = {
  apiKey: process.env.REACT_APP_AI_API_KEY || localStorage.getItem('ed_ai_key') || '',
  model: 'gpt-3.5-turbo',
  maxTokens: 1000,
  temperature: 0.7,
};

// ───────────────────────────────────────────────────────────────────
//  CONFIGURAÇÕES DO APLICATIVO
// ───────────────────────────────────────────────────────────────────
export const appConfig = {
  env: process.env.REACT_APP_ENV || 'development',
  debug: process.env.REACT_APP_DEBUG === 'true',
  syncTimeout: parseInt(process.env.REACT_APP_SYNC_TIMEOUT ?? '', 10) || 30000,

  // Storage keys
  storageKeys: {
    state: 'crm_state',
    settings: 'crm_settings',
    supabaseUrl: 'ed_supa_url',
    supabaseKey: 'ed_supa_key',
    n8nSummary: 'ed_n8n_summary',
    n8nVono: 'ed_n8n_vono',
    n8nCall: 'ed_n8n_call',
    aiKey: 'ed_ai_key',
  },
};

// ───────────────────────────────────────────────────────────────────
//  CONFIGURAÇÕES DE AGENDA
// ───────────────────────────────────────────────────────────────────
export const scheduleConfig = {
  slotTimes: ['09:30', '11:20', '14:20', '16:30'],
  businessDays: 5,
  blockedDates: [] as unknown[],
  // blockedDates: [{date:'2026-04-21', type:'feriado', allDay:true, timeFrom:'', timeTo:'', label:'Tiradentes'}]
};

// ───────────────────────────────────────────────────────────────────
//  CONFIGURAÇÕES DE PIPELINE
// ───────────────────────────────────────────────────────────────────
export const pipelineConfig = {
  defaultProduct: {
    id: 'prod1',
    name: 'Equity',
    color: '#2563C8',
    sdrPipeId: 'sdr1',
    closerPipeId: 'cl1',
  },

  defaultPipelines: [
    {
      id: 'sdr1',
      productId: 'prod1',
      type: 'sdr',
      name: 'SDR',
      stages: [
        { id: 'st1', name: 'Novo Lead', color: '#6B7280' },
        { id: 'st2', name: 'Em Contato', color: '#2563C8' },
        { id: 'st3', name: 'Qualificado', color: '#0891B2' },
        { id: 'st4', name: 'Agendado', color: '#7C3AED' },
        { id: 'st5', name: 'Realizada', color: '#059669' },
        { id: 'st6', name: 'Não Compareceu', color: '#DC2626' },
      ],
      lossReasons: ['Sem interesse', 'Não atende', 'Lead frio', 'Orçamento', 'Concorrência'],
    },
    {
      id: 'cl1',
      productId: 'prod1',
      type: 'closer',
      name: 'Closer',
      stages: [
        { id: 'cl1', name: 'Reunião', color: '#2563C8' },
        { id: 'cl2', name: 'Proposta', color: '#7C3AED' },
        { id: 'cl3', name: 'Negociação', color: '#0891B2' },
        { id: 'cl4', name: 'Contrato', color: '#059669' },
        { id: 'cl5', name: 'Venda', color: '#16A34A' },
        { id: 'cl6', name: 'Perdido', color: '#DC2626' },
      ],
      lossReasons: ['Preço', 'Concorrência', 'Sem orçamento', 'Decisão adiada', 'Produto inadequado'],
    },
  ],
};

// ───────────────────────────────────────────────────────────────────
//  ORIGENS DE LEADS
// ───────────────────────────────────────────────────────────────────
export const origins: string[] = [
  'Tráfego Pago',
  'Orgânico',
  'Indicação',
  'Instagram',
  'LinkedIn',
  'WhatsApp',
  'Formulário',
  'Evento',
  'Parceiro',
];

// ───────────────────────────────────────────────────────────────────
//  CANAIS E SUB-ORIGENS
// ───────────────────────────────────────────────────────────────────
export const channels: Record<string, string[]> = {
  'Tráfego Pago': ['Google Ads', 'Meta Ads', 'LinkedIn Ads', 'TikTok Ads'],
  'Orgânico': ['Google Search', 'Direto', 'Blog'],
  'Indicação': ['Cliente', 'Parceiro', 'Colaborador'],
  'Instagram': ['Post', 'Stories', 'Reels', 'Direct'],
  'LinkedIn': ['Post', 'Direto', 'Grupo'],
  'WhatsApp': ['Direto', 'Grupo', 'Bot'],
};

// ───────────────────────────────────────────────────────────────────
//  FUNÇÕES AUXILIARES
// ───────────────────────────────────────────────────────────────────
export const getSupabaseConfig = (): Pick<SupabaseConfig, 'url' | 'anonKey'> => {
  return {
    url: supabaseConfig.url,
    anonKey: supabaseConfig.anonKey,
  };
};

export const isSupabaseConfigured = (): boolean => {
  return !!(supabaseConfig.url && supabaseConfig.anonKey);
};

export const saveSupabaseConfig = (url: string, key: string): void => {
  localStorage.setItem(appConfig.storageKeys.supabaseUrl, url);
  localStorage.setItem(appConfig.storageKeys.supabaseKey, key);
  supabaseConfig.url = url;
  supabaseConfig.anonKey = key;
};

export const clearSupabaseConfig = (): void => {
  localStorage.removeItem(appConfig.storageKeys.supabaseUrl);
  localStorage.removeItem(appConfig.storageKeys.supabaseKey);
  supabaseConfig.url = '';
  supabaseConfig.anonKey = '';
};
